import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, ArrowRight, CheckCircle, BookOpen, Clock, 
  AlertCircle, Lightbulb, Code, TrendingUp, Shield 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Lesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  useEffect(() => {
    fetchLesson();
  }, [lessonId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchLesson = async () => {
    try {
      const res = await axios.get(`${API}/academy/lessons/${lessonId}`);
      setLesson(res.data);
    } catch (error) {
      console.error('Error fetching lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async () => {
    try {
      await axios.post(
        `${API}/academy/progress/${lesson.course_id}/${lessonId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Lesson completed! 🎉');
      
      // Navigate back to course
      navigate(`/academy/courses/${lesson.course_id}`);
    } catch (error) {
      toast.error('Failed to mark lesson complete');
    }
  };

  const submitQuiz = () => {
    if (!lesson.content.quiz) return;
    
    let correct = 0;
    lesson.content.quiz.forEach((q, idx) => {
      if (quizAnswers[idx] === q.correct) {
        correct++;
      }
    });
    
    const score = (correct / lesson.content.quiz.length) * 100;
    setQuizScore(score);
    setShowQuizResults(true);
    
    if (score >= 70) {
      toast.success(`Great job! You scored ${Math.round(score)}%`);
      markComplete();
    } else {
      toast.error(`You scored ${Math.round(score)}%. Need 70% to pass. Try again!`);
    }
  };

  const renderSection = (section, index) => {
    switch (section.type) {
      case 'text':
        return (
          <div key={index} className="mb-6">
            {section.title && <h3 className="text-2xl font-bold mb-3">{section.title}</h3>}
            <p className="text-gray-300 leading-relaxed">{section.content}</p>
          </div>
        );

      case 'comparison':
        return (
          <Card key={index} className="mb-6 bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {section.items.map((item, idx) => (
                  <div key={idx} className="grid md:grid-cols-2 gap-4 p-3 bg-secondary rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-red-400 font-semibold">Traditional:</span>
                      <span className="text-gray-300">{item.traditional}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-green-400 font-semibold">DeFi:</span>
                      <span className="text-gray-300">{item.defi || item.public}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'example':
        return (
          <Card key={index} className="mb-6 bg-blue-500/10 border-blue-500/30">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-blue-400" />
                <CardTitle className="text-lg">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 whitespace-pre-line">{section.content}</p>
            </CardContent>
          </Card>
        );

      case 'key_takeaway':
        return (
          <Card key={index} className="mb-6 bg-green-500/10 border-green-500/30">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <CardTitle className="text-lg">Key Takeaways</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {section.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span className="text-gray-300">{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );

      case 'warning':
        return (
          <Card key={index} className="mb-6 bg-red-500/10 border-red-500/30">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <CardTitle className="text-lg">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 whitespace-pre-line">{section.content}</p>
            </CardContent>
          </Card>
        );

      case 'step_by_step':
        return (
          <Card key={index} className="mb-6 bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {section.steps.map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
                      {step.number || idx + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1">{step.title || step}</h4>
                      {step.description && (
                        <p className="text-sm text-gray-400">{step.description}</p>
                      )}
                      {step.action && (
                        <p className="text-sm text-indigo-400 mt-1">{step.action}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'code_example':
        return (
          <Card key={index} className="mb-6 bg-gray-900 border-gray-700">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Code className="h-5 w-5 text-purple-400" />
                <CardTitle className="text-lg">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">{section.code}</pre>
            </CardContent>
          </Card>
        );

      case 'list':
        return (
          <Card key={index} className="mb-6 bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {section.items.map((item, idx) => (
                  <div key={idx} className="p-3 bg-secondary rounded-lg">
                    <h4 className="font-semibold text-indigo-400 mb-1">{item.term || item.use}</h4>
                    <p className="text-sm text-gray-300">{item.definition || item.benefit}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Lesson not found</h2>
          <Button onClick={() => navigate('/academy')}>Back to Academy</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      {/* Header */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/academy/courses/${lesson.course_id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Course
          </Button>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-400">{lesson.duration}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Lesson Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-6 w-6 text-indigo-400" />
            <Badge>{lesson.type}</Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{lesson.title}</h1>
          <p className="text-xl text-gray-400">{lesson.description}</p>
        </div>

        {/* Lesson Content */}
        <div className="space-y-6">
          {lesson.content?.sections?.map((section, index) => renderSection(section, index))}
        </div>

        {/* Quiz Section */}
        {lesson.content?.quiz && lesson.content.quiz.length > 0 && (
          <Card className="mt-12 bg-purple-500/10 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-2xl">Check Your Understanding</CardTitle>
              <CardDescription>Answer these questions to complete the lesson (70% to pass)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {lesson.content.quiz.map((question, qIdx) => (
                <div key={qIdx} className="p-4 bg-secondary rounded-lg">
                  <p className="font-semibold mb-3">{qIdx + 1}. {question.question}</p>
                  <div className="space-y-2">
                    {question.options.map((option, oIdx) => (
                      <label 
                        key={oIdx}
                        className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-all ${
                          quizAnswers[qIdx] === oIdx 
                            ? 'bg-indigo-600/30 border border-indigo-500' 
                            : 'bg-white/5 hover:bg-white/10'
                        } ${
                          showQuizResults && question.correct === oIdx
                            ? 'border-2 border-green-500'
                            : ''
                        } ${
                          showQuizResults && quizAnswers[qIdx] === oIdx && question.correct !== oIdx
                            ? 'border-2 border-red-500'
                            : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${qIdx}`}
                          checked={quizAnswers[qIdx] === oIdx}
                          onChange={() => setQuizAnswers({ ...quizAnswers, [qIdx]: oIdx })}
                          disabled={showQuizResults}
                          className="w-4 h-4"
                        />
                        <span>{option}</span>
                        {showQuizResults && question.correct === oIdx && (
                          <CheckCircle className="h-5 w-5 text-green-400 ml-auto" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {showQuizResults && (
                <Card className={quizScore >= 70 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}>
                  <CardContent className="pt-6 text-center">
                    <h3 className="text-2xl font-bold mb-2">
                      {quizScore >= 70 ? '🎉 Passed!' : '📚 Try Again'}
                    </h3>
                    <p className="text-xl mb-4">Your Score: {Math.round(quizScore)}%</p>
                    {quizScore >= 70 ? (
                      <p className="text-gray-300">Great job! Lesson marked as complete.</p>
                    ) : (
                      <div>
                        <p className="text-gray-300 mb-4">Review the content and try again.</p>
                        <Button onClick={() => { setShowQuizResults(false); setQuizAnswers({}); }}>
                          Retry Quiz
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {!showQuizResults && (
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length !== lesson.content.quiz.length}
                >
                  Submit Quiz <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Complete Lesson Button (if no quiz) */}
        {(!lesson.content?.quiz || lesson.content.quiz.length === 0) && (
          <div className="mt-12 flex justify-center">
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700"
              onClick={markComplete}
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Mark Lesson Complete
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lesson;
